import React, { FC, memo, useCallback, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useHistory, useParams } from 'react-router-dom';
import NumberInput from '~ComponentsImproved/Numpad';
import { useI18n, useLogger } from '~Hooks';

/** How many taps in the corner open the code prompt. */
const TAPS_TO_OPEN = 4;
/** Taps further apart than this are treated as a fresh attempt. */
const TAP_WINDOW_MS = 2000;
/** Same staff code as the self service kiosk's GetBack. */
const PASS = '1111';

/** Invisible hit area in the top left corner of the stage. */
const GetBackArea = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 5;
  width: 50%;
  height: 300px;
`;

/**
 * Hidden staff exit, mirroring the self service kiosk: tap the top left corner
 * four times to get a code prompt that leads back to the gateway. The tap
 * counter lives in a ref so tapping never re-renders anything - only opening
 * and closing the prompt does.
 */
const GetBack: FC = () => {
  const history = useHistory();
  const params = useParams<{ id: string }>();
  const logger = useLogger('epos-get-back');
  const { i18n } = useI18n();

  const tapCountRef = useRef<number>(0);
  const lastTapRef = useRef<number>(0);
  const [showCodeInput, setShowCodeInput] = useState<boolean>(false);

  const handleTap = useCallback(() => {
    const now = Date.now();
    if (now - lastTapRef.current > TAP_WINDOW_MS) {
      tapCountRef.current = 0;
    }
    lastTapRef.current = now;
    tapCountRef.current += 1;

    if (tapCountRef.current >= TAPS_TO_OPEN) {
      logger.info('Staff exit code prompt opened');
      tapCountRef.current = 0;
      setShowCodeInput(true);
    }
  }, []);

  const handleClose = useCallback(() => setShowCodeInput(false), []);

  const handleSubmit = useCallback(
    (value: string) => {
      if (value !== PASS) {
        logger.warn('Wrong staff exit code entered');
        return;
      }
      logger.info('Leaving EPOS for the gateway', { venueId: params.id });
      setShowCodeInput(false);
      // Let the numpad close before the route changes.
      setTimeout(() => history.push(`/venue/${params.id}/gateway`), 500);
    },
    [history, params.id],
  );

  return (
    <>
      <GetBackArea onTouchStart={handleTap} data-testid="epos-get-back-area" />
      {showCodeInput && (
        <NumberInput
          decimal
          title={i18n('SSK.EnterTheCode')}
          onClose={handleClose}
          onSubmit={handleSubmit}
        />
      )}
    </>
  );
};

export default memo(GetBack);
